import api from '../../../services/api';

class NotificationService {
  // Cache for unread count requests to prevent duplicate simultaneous calls
  _unreadCountCache = null;
  _unreadCountPromise = null;
  _unreadCountCacheTime = 0;
  _unreadCountCacheTTL = 30000; // 30 seconds cache (notifications change less frequently)

  /**
   * Get user notifications
   */
  async getNotifications({ page = 1, limit = 20, unreadOnly = false } = {}) {
    const response = await api.get('/notifications', {
      params: { page, limit, unreadOnly },
    });
    // API interceptor already extracts data, so response is { success, message, data: { notifications, pagination, unreadCount } }
    return response.data;
  }

  /**
   * Get unread notification count
   * Uses caching and request deduplication to prevent duplicate simultaneous calls
   */
  async getUnreadCount(forceRefresh = false) {
    try {
      const now = Date.now();
      
      // Return cached data if still valid and not forcing refresh
      if (!forceRefresh && this._unreadCountCache !== null && (now - this._unreadCountCacheTime) < this._unreadCountCacheTTL) {
        return this._unreadCountCache;
      }
      
      // If there's already a pending request, return that promise instead of making a new one
      if (this._unreadCountPromise) {
        return this._unreadCountPromise;
      }
      
      // Make the request and cache the promise
      this._unreadCountPromise = api.get('/notifications/unread-count')
        .then(response => {
          // The API response structure might be different
          let count = 0;
          if (typeof response.data === 'number') {
            count = response.data;
          } else if (response.data && typeof response.data === 'object') {
            count = response.data.count || response.data.unreadCount || 0;
          }
          
          // Cache the result with current timestamp
          this._unreadCountCache = count;
          this._unreadCountCacheTime = Date.now();
          // Clear the promise so future calls can make new requests
          this._unreadCountPromise = null;
          return count;
        })
        .catch(error => {
          // Clear the promise on error so retries can happen
          this._unreadCountPromise = null;
          throw error;
        });
      
      return this._unreadCountPromise;
    } catch (error) {
      this._unreadCountPromise = null;
      throw error;
    }
  }
  
  /**
   * Clear unread count cache (useful after marking notifications as read)
   */
  clearUnreadCountCache() {
    this._unreadCountCache = null;
    this._unreadCountCacheTime = 0;
    this._unreadCountPromise = null;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    const response = await api.patch(`/notifications/${notificationId}/read`);
    // Clear cache after marking as read
    this.clearUnreadCountCache();
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    const response = await api.patch('/notifications/mark-all-read');
    // Clear cache after marking all as read
    this.clearUnreadCountCache();
    return response.data;
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  }
}

const notificationServiceInstance = new NotificationService();
export default notificationServiceInstance;

